import { formatNumber } from '../app/modules/utilities'

import { chartPalette, cssVar } from './palette'

// Chart.js plugin that renders each row's total as a flush right-aligned column
// pinned to the chart's right edge — the value column on the horizontal "ranked
// breakdown" bars (see BasePresenter.ranked_breakdown_bar_chart_js).
//
// This is used instead of datalabels plugin as it allows for more control over
// the positioning and styling totals column
export const TotalsAxisPlugin = {
    id: 'totalsAxis',

    beforeInit(chart) {
        TotalsAxisPlugin.beforeUpdate(chart)
    },

    beforeUpdate(chart) {
        const scales = chart.options.scales
        if (!scales) return

        scales.y2 ??= {
            type: 'category',
            position: 'right',
            offset: true, // center ticks on rows, matching the bar (index) axis
            grid: { display: false, drawTicks: false },
            border: { display: false },
            ticks: {
                crossAlign: 'far', // flush right-aligned column
                // Monospace (Bootstrap's stack) so the digits column stays tabular.
                font: { weight: 600, size: 11, family: cssVar('--bs-font-monospace', 'monospace') },
            },
        }

        scales.y2.ticks.color = chartPalette().totalInk

        // The category labels on the left get the same ink as the totals column,
        // overriding the dimmer global tick color — only ranked bars opt into
        // this plugin, so other charts keep the default.
        scales.y ??= {}
        scales.y.ticks ??= {}
        scales.y.ticks.color = chartPalette().labelInk

        // Hidden for the empty "No Data" placeholder (charts_js_controller swaps in
        // a dataset with no values); flips back when a reload brings real data.
        scales.y2.display = hasAnyValue(chart.data.datasets)
        scales.y2.labels = totalsLabels(chart)
    },
}

function hasAnyValue(datasets) {
    return (datasets ?? []).some((ds) => (ds.data ?? []).some((v) => rawValue(v)))
}

// Per-row totals over the visible datasets, formatted with the datasets' shared
// formatter — the same summing/formatter rules as the datalabels formatter in
// charts_js_controller (which pies still use for on-slice labels).
function totalsLabels(chart) {
    const { labels = [], datasets = [] } = chart.data
    const totals = new Array(labels.length).fill(0)

    datasets.forEach((ds, idx) => {
        if (!chart.isDatasetVisible(idx)) return
        for (let i = 0; i < totals.length; i++) {
            totals[i] += rawValue(ds.data?.[i])
        }
    })

    // We need the same formatter for all datasets in order to format the summed totals
    let formatter
    const formatters = [...new Set(datasets.map((ds) => ds.formatter))]
    if (formatters.length > 1) {
        console.error('There are more than one formatter and cannot apply format: ', formatters)
    } else {
        formatter = formatters[0]
    }

    return totals.map((total) => formatNumber(total, formatter))
}

// Data points come in three shapes: raw number, {x} / {y} objects, or absent.
function rawValue(v) {
    if (v == null) return 0
    return typeof v === 'object' ? (v.x ?? v.y ?? 0) : +v || 0
}
